(ns renolit-roofing-service.view.project-list
  (:require [ajax.core :as ajax]
            [clojure.string :as str]
            [dev.gethop.session.re-frame.cognito.token :as cognito.token]
            [reagent.core :as r]
            [re-frame.core :as rf]
            [renolit-roofing-service.entity-list :as entity-list]
            [renolit-roofing-service.layout.form :as layout.form]
            [renolit-roofing-service.layout.grid-table :as layout.grid-table]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.layout.info-bar :as layout.info-bar]
            [renolit-roofing-service.layout.user-layout :as user-layout]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.project :as srv.project]
            [renolit-roofing-service.shared.domain.types :as dom.types]
            [renolit-roofing-service.util :as util]
            [renolit-roofing-service.util.file :as util.file]
            [renolit-roofing-service.util.localization :as util.localization]
            [renolit-roofing-service.util.react-date-range :as util.time]
            [renolit-roofing-service.view :as view]
            [renolit-roofing-service.view.project-filters-popup :as view.project-filters-popup]))

(def ^:const entity-key :project-list)

(def ^:const view-context
  :projects-list-view)

(defn- decode-applied-filters-from-query-params
  [{:keys [products statuses requires-review aged-gt-10-years]}]
  (cond-> {}
    (not-empty products) (assoc :products (into #{} (str/split products #",")))
    (not-empty statuses) (assoc :statuses (into #{} (map keyword (str/split statuses #","))))
    (= "true" requires-review) (assoc :requires-review? true)
    (= "true" aged-gt-10-years) (assoc :aged-gt-10-years? true)))

(defn build-get-projects-query-params
  [db]
  (let [{:keys [products statuses requires-review? aged-gt-10-years?]} (:projects-list/applied-filters db)
        search (:projects-list/search db)]
    (cond-> {:cols [:id :number :name :account-name :product :warranty-number :building-state :installation-date]}
      (not-empty search) (assoc-in [:filters :search] search)
      (not-empty products) (assoc-in [:filters :products] products)
      (not-empty statuses) (assoc-in [:filters :statuses] statuses)
      requires-review? (assoc-in [:filters :requires-review?] true)
      aged-gt-10-years? (assoc-in [:filters :aged-gt-10-years?] true))))

(rf/reg-event-fx
 ::load-list-data
 [(rf/inject-cofx ::cognito.token/session)]
 (fn [{:keys [db]} [_ opts]]
   {:fx [[:dispatch [::entity-list/load-list
                     (merge
                      {:entity-key entity-key
                       :uri "/api/projects"
                       :extra-params (build-get-projects-query-params db)}
                      opts)]]]}))

(rf/reg-event-fx
 ::export-projects-csv
 [(rf/inject-cofx ::cognito.token/session)]
 (fn [{:keys [db session]} _]
   {:http-xhrio
    {:headers {"Authorization" (str "Bearer " (get-in session [:id-token :jwt]))}
     :method :get
     :uri "/api/projects-csv-export"
     :params (build-get-projects-query-params db)
     :response-format (ajax/text-response-format)
     :on-success [::export-projects-csv-success]
     :on-failure [::export-projects-csv-failure]}}))

(rf/reg-event-fx
 ::export-projects-csv-success
 (fn [_ [_ csv-text]]
   (util.file/trigger-csv-download!
    "projects.csv"
    csv-text)
   {}))

(rf/reg-event-fx
 ::export-projects-csv-failure
 (fn [_ [_ {:keys [response]}]]
   (let [{:keys [reason]} (util/json->clj response)]
     {:dispatch
      [::layout.info-bar/add-info-bar
       {:context view-context
        :disappear-in-ms 5000
        :type :error
        :msg-transl-kw
        (case reason
          "could-not-create-csv-file" :error-msg.could-not-create-csv-file
          :error-msg.unknown)}]})))

(def ^:const route-config
  {:name [:project-list]
   :auth-req? true
   :controllers [{:start #(rf/dispatch [::view.on-enter %])
                  :stop #(rf/dispatch [::view.on-leave %])}]})

(rf/reg-event-fx
 ::view.on-enter
 [(rf/inject-cofx ::cognito.token/session)]
 (fn [{:keys [db]} _]
   (let [{:keys [search] :as query-params} (get-in db [:current-route :parameters :query])
         applied-filters (decode-applied-filters-from-query-params query-params)]
     {:db (cond-> db
            (not-empty search) (assoc :projects-list/search search)
            (seq applied-filters) (assoc :projects-list/applied-filters applied-filters))
      :fx [[:dispatch [::load-list-data]]
           [:dispatch [::srv.project/get-projects-stats]]]})))

(rf/reg-event-fx
 ::view.on-leave
 (fn [{:keys [db]} _]
   {:db (dissoc db :projects-list/filters :projects-list/applied-filters :projects-list/search)
    :fx [[:dispatch [::entity-list/clean-entity-data]]
         [:dispatch [::srv.project/clear-projects-stats]]]}))

(rf/reg-sub
 ::search
 (fn [db]
   (get db :projects-list/search)))

(rf/reg-event-fx
 ::set-search
 (fn [{:keys [db]} [_ search]]
   {:db (assoc db :projects-list/search search)
    :fx [[:dispatch [::load-list-data]]
         [:dispatch [::nav/add-query-params nil {:search (not-empty search)} :replace-route? true]]]}))

(defn- filters-applied?
  [{:keys [products statuses requires-review? aged-gt-10-years?]}]
  (boolean (or (seq products) (seq statuses) requires-review? aged-gt-10-years?)))

(defn- status-summary
  []
  (let [stats @(rf/subscribe [::srv.project/projects-stats])]
    [:div.project-list-status-summary
     (map
      (fn [status]
        ^{:key status}
        [:div.project-list-status-summary__item
         [:div.project-list-status-summary__item-inner
          (str (util.localization/dropdown-value :service-status status) " (" (get stats status 0) ")")]])
      dom.types/service-status-pipeline)]))

(defn- main*
  []
  (let [initial-applied-filters @(rf/subscribe [::view.project-filters-popup/applied-filters])
        search @(rf/subscribe [::search])
        state (r/atom {:products {:checked {}
                                  :selected #{}}
                       :statuses {:checked {}
                                  :selected #{}}
                       :requires-review {:selected (boolean (:requires-review? initial-applied-filters))}
                       :aged-gt-10-years {:selected (boolean (:aged-gt-10-years? initial-applied-filters))}})
        projects (rf/subscribe [::entity-list/list entity-key])
        applied-filters (rf/subscribe [::view.project-filters-popup/applied-filters])]
    (fn []
      [:div.project-list
       [status-summary]
       [:div.project-list-toolbar
        [layout.form/item
         {:type :search
          :placeholder-transl-kw :placeholder.search
          :state (atom {:search search})
          :path [:search]
          :on-change {:fn #(rf/dispatch [::set-search %])}
          :css-class-prefix "project-list-toolbar__search"}]
        [:div.project-list-toolbar__actions
         [:button.btn.project-list-btn--secondary
          {:class (when (filters-applied? @applied-filters) "project-list-btn--active")
           :on-click (fn []
                       (rf/dispatch
                        [::view.project-filters-popup/open
                         {:state state
                          :on-submit #(rf/dispatch [::load-list-data])}]))}
          [layout.icon/icon {:key :flask}] (t [:page.project.list.filters])]
         [:button.btn.project-list-btn--secondary {:on-click #(rf/dispatch [::export-projects-csv])}
          [layout.icon/icon {:key :export}]]]]
       [:div.project-list-grid-section
        [layout.grid-table/main
         {:data @projects
          :grid-header-extra-class "grid-table__header--project-list-"
          :grid-row-extra-class "grid-table__row--project-list-"
          :row-href-fn #(nav/href [:project] {:project-id (:id %)})
          :cols [{:id :number
                  :path [:number]
                  :title-transl-kw :page.project.list.table-header.number
                  :cell-settings {:type :text}}
                 {:id :name
                  :path [:name]
                  :title-transl-kw :page.project.list.table-header.name
                  :cell-settings {:type :text}}
                 {:id :account-name
                  :path [:account-name]
                  :title-transl-kw :page.project.list.table-header.account-name
                  :cell-settings {:type :text}}
                 {:id :warranty-number
                  :path [:warranty-number]
                  :title-transl-kw :page.project.list.table-header.warranty-number
                  :cell-settings {:type :text}}
                 {:id :province
                  :path [:building-state]
                  :title-transl-kw :page.project.list.table-header.building-state
                  :cell-settings {:type :text}}
                 {:id :project-installation-date
                  :path [:installation-date]
                  :title-transl-kw :page.project.list.table-header.installation-date
                  :cell-settings {:type :custom
                                  :component (fn [project-installation-date]
                                               (let [local-date (util.time/lib-date->date :local-date nil project-installation-date)
                                                     formatted (util.time/format-date :local-date nil local-date)]
                                                 [:div.grid-table__cell
                                                  [:div.media-grid-item
                                                   [:span
                                                    formatted]]]))}}
                 {:id :nav-button
                  :path [:id]
                  :cell-settings {:type :custom
                                  :component (fn [_]
                                               [layout.icon/icon {:class ["arrow-link"]
                                                                  :key :right-arrow}])}}]}]]])))

(defn- main
  []
  [user-layout/main
   {:header-title-kw :page.project.list.title}
   [main*]])

(defmethod view/view-display (:name route-config)
  [_]
  [main])
